import type { FC, FormEvent } from 'react';
import { createRef, useEffect, useState } from 'react';

import useDispatch from '@app/hooks/useDispatch';
import useSelector from '@app/hooks/useSelector';
import { InputErrorMessage } from '@lokalise/louis';
import { AuthInput, AuthLabel, Button, CommonError, FormRow, Modal } from '../../components';
import { getErrorForField, isCommonError } from '../../helpers';
import { loginSelector } from '../../selectors';
import type { ResetModalData } from './LinkSentModal';
import { loginActions } from './reducers';

interface ForgetPassModalProps {
    onClose: () => void;
    onSuccess: (data: ResetModalData) => void;
}

const ForgetPassModal: FC<ForgetPassModalProps> = ({ onClose, onSuccess }) => {
    const dispatch = useDispatch();
    const {
        forgetModal: { loading, error },
    } = useSelector(loginSelector);
    const [email, setEmail] = useState('');
    const inputRef = createRef<HTMLInputElement>();

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (loading) {
            return;
        }
        dispatch(
            loginActions.forgetPasswordRequest(email, (response) => {
                onSuccess(response.data);
            }),
        );
    };

    useEffect(() => inputRef?.current?.focus());

    return (
        <Modal title="Forgot password?" onCloseHandler={onClose}>
            <form onSubmit={handleSubmit}>
                <FormRow>
                    <AuthLabel text="Your e-mail" />
                    <AuthInput
                        ref={inputRef}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="user@company.com"
                    />
                    {getErrorForField(error, 'email')?.map((msg) => (
                        <InputErrorMessage id="emailError" key={msg}>
                            {msg}
                        </InputErrorMessage>
                    ))}
                </FormRow>
                {isCommonError(error) && (
                    <CommonError>
                        <InputErrorMessage id="commonError">{error?.message}</InputErrorMessage>
                    </CommonError>
                )}
                <Button loading={loading} type="submit" variant="primary">
                    Send me the reset link
                </Button>
            </form>
        </Modal>
    );
};

export default ForgetPassModal;
